"use client";

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { apiConfig } from "../config/api.config";
import BackToMenu from "../components/BackToMenu.jsx";
import { useAuth } from "../context/AuthContext";
import { FileText, RefreshCw, Calendar, Clock, CheckCircle, XCircle } from "lucide-react";
import PresupuestoSolicitudesModal from "../components/PresupuestoSolicitudesModal.jsx";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (value) => {
  if (!value) return "—";
  const str = String(value).trim();
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString("es-CO");
  const dt = new Date(str);
  return isNaN(dt.getTime()) ? str : dt.toLocaleDateString("es-CO");
};

const normalizeStatus = (s) =>
  String(s || "").trim().toLowerCase().replace(/\s+/g, "_");

const getStatusInfo = (status) => {
  const s = normalizeStatus(status);
  if (["aprobado", "approved", "completed", "respondido"].includes(s))
    return { bg: "#dcfce7", color: "#166534", label: "Respondido", icon: "✅" };
  if (["negado", "rechazado", "rejected"].includes(s))
    return { bg: "#fee2e2", color: "#b91c1c", label: "Negado", icon: "❌" };
  return { bg: "#fef9c3", color: "#854d0e", label: "Pendiente", icon: "⏳" };
};

const getTipoBadge = (tipo) => {
  switch (String(tipo).toUpperCase()) {
    case "CDP": return { bg: "#fef9c3", color: "#854d0e", label: "CDP" };
    case "RP":  return { bg: "#dcfce7", color: "#166534", label: "RP" };
    default:    return { bg: "#f3f4f6", color: "#374151", label: tipo };
  }
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function SolicitadosCDPRP() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMountedRef = useRef(true);

  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Modal de detalle — reutiliza PresupuestoSolicitudesModal
  // Le pasamos un "contrato virtual" con es_otras_cdp_rp=true y el id del group
  const [modalContrato, setModalContrato] = useState(null);

  useEffect(() => {
    isMountedRef.current = true;
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => {
      isMountedRef.current = false;
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const isMobile = windowWidth < 768;

  // ── Carga ────────────────────────────────────────────────────────────────────

  const loadSolicitudes = async () => {
    if (!isMountedRef.current) return;
    setLoading(true);
    setError(null);

    try {
      // Traemos TODAS las solicitudes de otras_cdp_rp
      const url = apiConfig.getUrl(
        "/api/presupuesto/solicitudes/index.php",
        "source_module=otras_cdp_rp"
      );

      const res = await fetch(url, {
        credentials: "include",
        cache: "no-store",
        headers: { Accept: "application/json" },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const json = await res.json();
      const rows = json.solicitudes || json.data || json || [];
      const arr  = Array.isArray(rows) ? rows : [rows];

      const filtered = arr
        .filter((r) =>
          String(r.source_module || "").toLowerCase().trim() === "otras_cdp_rp"
        )
        .map((r) => ({
          ...r,
          request_type: String(r.request_type || "").toUpperCase(),
          sent_at: r.sent_at || r.created_at || null,
        }))
        .sort((a, b) => new Date(b.sent_at || 0) - new Date(a.sent_at || 0));

      if (isMountedRef.current) setSolicitudes(filtered);
    } catch (err) {
      console.error("Error cargando solicitados:", err);
      if (isMountedRef.current) setError("Error al cargar las solicitudes.");
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    loadSolicitudes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Abrir modal de detalle ───────────────────────────────────────────────────
  // PresupuestoSolicitudesModal espera un "contrato" con id y es_otras_cdp_rp=true
  const handleVerDetalle = (sol) => {
    let formData = {};
    try {
      formData =
        typeof sol.form_data === "string" ? JSON.parse(sol.form_data) : sol.form_data || {};
    } catch { formData = {}; }

    setModalContrato({
      id: sol.contract_id || sol.contractId || sol.id,
      es_otras_cdp_rp: true,
      solicitud_id: sol.id,
      nombre_contratista:
        formData.nombre_beneficiario || formData.correo || `Solicitud ${sol.request_type}`,
    });
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        background: "linear-gradient(to bottom, #1e3a8a, #1e40af)",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          flex: 1,
          padding: isMobile ? "1rem" : "2rem",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            maxWidth: "1000px",
            width: "100%",
            backgroundColor: "white",
            borderRadius: isMobile ? "0.5rem" : "1rem",
            boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
            overflow: "hidden",
          }}
        >
          {/* Back */}
          <div
            style={{
              padding: isMobile ? "0.75rem" : "1rem",
              borderBottom: "1px solid #e5e7eb",
              backgroundColor: "#f9fafb",
            }}
          >
            <BackToMenu
              label="← Volver"
              to="/otras-necesidades-cdp-rp"
            />
          </div>

          {/* Header */}
          <div
            style={{
              padding: isMobile ? "1rem" : "1.5rem",
              borderBottom: "1px solid #e5e7eb",
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "flex-start" : "center",
              gap: "0.75rem",
            }}
          >
            <div>
              <h2
                style={{
                  fontSize: isMobile ? "1.2rem" : "1.4rem",
                  fontWeight: 800,
                  color: "#1e3a8a",
                  margin: 0,
                }}
              >
                CDPs y RPs Solicitados
              </h2>
              <p style={{ margin: "4px 0 0", color: "#6b7280", fontSize: "0.875rem" }}>
                Historial de formularios enviados a Presupuesto
              </p>
            </div>

            <button
              onClick={loadSolicitudes}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.4rem",
                backgroundColor: "#e5e7eb",
                color: "#374151",
                padding: "0.5rem 1rem",
                borderRadius: "9999px",
                border: "none",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "0.875rem",
                flexShrink: 0,
              }}
            >
              <RefreshCw size={14} />
              Actualizar
            </button>
          </div>

          {/* Content */}
          <div style={{ padding: isMobile ? "0.75rem" : "1.5rem" }}>
            {loading ? (
              <p style={{ textAlign: "center", color: "#6b7280", padding: "2rem" }}>
                Cargando...
              </p>
            ) : error ? (
              <p style={{ textAlign: "center", color: "#dc2626", padding: "2rem" }}>
                {error}
              </p>
            ) : solicitudes.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "3rem",
                  color: "#6b7280",
                  backgroundColor: "#f9fafb",
                  borderRadius: "0.5rem",
                }}
              >
                <FileText
                  size={48}
                  style={{ margin: "0 auto 1rem", color: "#9ca3af" }}
                />
                <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>
                  No has enviado solicitudes aún
                </p>
                <p style={{ fontSize: "0.875rem" }}>
                  Usa "Radicar CDP" o "Radicar RP" para enviar un formulario.
                </p>
                <button
                  onClick={() => navigate("/otras-necesidades-cdp-rp")}
                  style={{
                    marginTop: "1rem",
                    backgroundColor: "#1e40af",
                    color: "white",
                    border: "none",
                    padding: "0.6rem 1.25rem",
                    borderRadius: "9999px",
                    cursor: "pointer",
                    fontWeight: 700,
                    fontSize: "0.875rem",
                  }}
                >
                  Ir al menú
                </button>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "separate",
                    borderSpacing: 0,
                    borderRadius: "0.5rem",
                    overflow: "hidden",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    fontSize: isMobile ? "0.75rem" : "0.85rem",
                    minWidth: isMobile ? "520px" : "auto",
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "#1e40af", color: "white" }}>
                      {["Fecha", "Tipo", "Dependencia", "Objeto / Beneficiario", "Estado", "Detalle"].map((h) => (
                        <th
                          key={h}
                          style={{
                            padding: isMobile ? "0.5rem 0.4rem" : "0.75rem 0.75rem",
                            textAlign: "left",
                            fontSize: isMobile ? "0.68rem" : "0.73rem",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {solicitudes.map((sol, idx) => {
                      const tipo       = sol.request_type;
                      const tipoBadge  = getTipoBadge(tipo);
                      const statusInfo = getStatusInfo(sol.response_status || sol.status);

                      let formData = {};
                      try {
                        formData =
                          typeof sol.form_data === "string"
                            ? JSON.parse(sol.form_data)
                            : sol.form_data || {};
                      } catch { formData = {}; }

                      const dependencia = formData.dependencia || "—";
                      const objeto =
                        formData.objeto || formData.nombre_beneficiario || formData.correo || "—";

                      return (
                        <tr
                          key={sol.id || idx}
                          style={{ backgroundColor: idx % 2 === 0 ? "white" : "#f9fafb" }}
                        >
                          {/* Fecha */}
                          <td
                            style={{
                              padding: isMobile ? "0.5rem 0.4rem" : "0.75rem 0.75rem",
                              borderBottom: "1px solid #e5e7eb",
                              whiteSpace: "nowrap",
                              color: "#1e40af",
                            }}
                          >
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                              <Calendar size={13} />
                              {formatDate(sol.sent_at)}
                            </span>
                          </td>

                          {/* Tipo */}
                          <td style={{ padding: isMobile ? "0.5rem 0.4rem" : "0.75rem 0.75rem", borderBottom: "1px solid #e5e7eb" }}>
                            <span
                              style={{
                                padding: "0.2rem 0.6rem",
                                borderRadius: "9999px",
                                fontSize: "0.72rem",
                                fontWeight: 700,
                                backgroundColor: tipoBadge.bg,
                                color: tipoBadge.color,
                              }}
                            >
                              {tipoBadge.label}
                            </span>
                          </td>

                          {/* Dependencia */}
                          <td
                            style={{
                              padding: isMobile ? "0.5rem 0.4rem" : "0.75rem 0.75rem",
                              borderBottom: "1px solid #e5e7eb",
                              maxWidth: 130,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={dependencia}
                          >
                            {dependencia}
                          </td>

                          {/* Objeto */}
                          <td
                            style={{
                              padding: isMobile ? "0.5rem 0.4rem" : "0.75rem 0.75rem",
                              borderBottom: "1px solid #e5e7eb",
                              maxWidth: 180,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={objeto}
                          >
                            {objeto}
                          </td>

                          {/* Estado */}
                          <td style={{ padding: isMobile ? "0.5rem 0.4rem" : "0.75rem 0.75rem", borderBottom: "1px solid #e5e7eb" }}>
                            <span
                              style={{
                                padding: "0.2rem 0.6rem",
                                borderRadius: "9999px",
                                fontSize: "0.72rem",
                                fontWeight: 600,
                                backgroundColor: statusInfo.bg,
                                color: statusInfo.color,
                              }}
                            >
                              {statusInfo.icon} {statusInfo.label}
                            </span>
                          </td>

                          {/* Detalle */}
                          <td
                            style={{
                              padding: isMobile ? "0.5rem 0.4rem" : "0.75rem 0.75rem",
                              borderBottom: "1px solid #e5e7eb",
                              textAlign: "center",
                            }}
                          >
                            <button
                              onClick={() => handleVerDetalle(sol)}
                              style={{
                                backgroundColor: "#0e7490",
                                color: "white",
                                border: "none",
                                padding: "0.4rem 0.85rem",
                                borderRadius: "0.375rem",
                                fontSize: "0.75rem",
                                fontWeight: 700,
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                              }}
                            >
                              Ver
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de detalle — reutiliza PresupuestoSolicitudesModal */}
      {modalContrato && (
        <PresupuestoSolicitudesModal
          show={Boolean(modalContrato)}
          contrato={modalContrato}
          onClose={() => {
            setModalContrato(null);
            loadSolicitudes();
          }}
          userRole="solicitante"
        />
      )}
    </div>
  );
}